import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import QRCode from "qrcode";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";
import { StatusBadge, WritingBadge } from "../components/StatusBadge";
import { fmtDate, fmtDateTime, fmtScore, prettyAnswer } from "../lib/format";
import { printCandidateSheet, windowText } from "../lib/printSheet";
import type { AnswerRow } from "../api/types";

export default function AssessmentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data: me } = useQuery({ queryKey: ["me"], queryFn: api.me });
  const { data: a, error, isLoading } = useQuery({
    queryKey: ["assessment", id],
    queryFn: () => api.assessments.get(id!),
    enabled: !!id,
  });

  const resendMut = useMutation({
    mutationFn: () => api.assessments.resendLink(id!),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["assessment", id] }),
  });
  const deleteMut = useMutation({
    mutationFn: () => api.assessments.remove(id!),
    onSuccess: () => navigate("/"),
  });

  const [reportUrl, setReportUrl] = useState<string | null>(null);
  const openReport = async () => {
    const url = await api.assessments.reportUrl(id!);
    setReportUrl(url);
  };

  if (isLoading) return <p className="text-slate-400">Loading...</p>;
  if (error) return <p className="text-liaise-red text-sm">{String(error)}</p>;
  if (!a) return null;

  const grouped: Record<string, AnswerRow[]> = {};
  for (const ans of a.answers) (grouped[ans.section] ??= []).push(ans);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <Link to="/" className="text-sm text-liaise-blue hover:underline">← Assessments</Link>
          <h1 className="text-2xl font-bold text-liaise-navy mt-1">
            {a.first_name} {a.last_name}
          </h1>
          <div className="flex items-center gap-2 mt-1 text-sm text-slate-500">
            <span className="font-mono">{a.test_id}</span>
            <StatusBadge status={a.status} />
            <WritingBadge result={a.writing_result} />
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {a.status === "submitted" && (
            <Link to={`/evaluate?testId=${a.test_id}`} className="btn-primary">Evaluate writing</Link>
          )}
          {a.status === "evaluated" && (
            <button className="btn-secondary" onClick={openReport}>View report</button>
          )}
          {me?.is_admin && (
            <button
              className="btn-danger"
              onClick={() => {
                if (confirm("Delete this assessment and all its answers? This cannot be undone.")) deleteMut.mutate();
              }}
              disabled={deleteMut.isPending}
            >
              Delete
            </button>
          )}
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <div className="card p-5 md:col-span-2 grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
          <Field
            label="Email"
            value={
              a.email
                ? a.attempts && a.attempts.total > 1
                  ? `${a.email}  ·  ${a.attempts.total} sittings (${a.attempts.completed} completed)`
                  : a.email
                : "-"
            }
          />
          <Field label="Contact" value={a.contact_number ?? "-"} />
          <Field label="Home" value={a.home ?? "-"} />
          <Field label="Region" value={a.region ?? "-"} />
          <Field label="Position" value={a.position ?? "-"} />
          <Field label="Assessment date" value={fmtDate(a.assessment_date)} />
          <Field label="Created by" value={a.created_by_name ?? "-"} />
          <Field label="Submitted" value={fmtDateTime(a.submitted_at)} />
          {a.status === "evaluated" && (
            <>
              <Field label="Evaluated by" value={a.reviewed_by_name ?? "-"} />
              <Field label="Evaluated at" value={fmtDateTime(a.reviewed_at)} />
            </>
          )}
        </div>
        <div className="card p-5 space-y-3">
          <ScoreRow label="English" value={fmtScore(a.english_total, a.english_max)} />
          <ScoreRow label="Maths" value={fmtScore(a.maths_total, a.maths_max)} />
          <ScoreRow label="Overall" value={fmtScore(a.grand_total, a.grand_max)} strong />
        </div>
      </div>

      {a.candidate_url && (
        <div className="card p-5 flex items-center justify-between flex-wrap gap-4">
          <LinkQr url={a.candidate_url} />
          <div className="text-sm flex-1 min-w-[200px]">
            <div className="font-semibold text-liaise-navy">Candidate link</div>
            <div className="text-xs text-slate-500 break-all">{a.candidate_url}</div>
            <div className="text-xs text-slate-400">
              Expires {fmtDateTime(a.expires_at)} · candidate scans the code or opens the link, no account needed
            </div>
            <div className="text-sm text-liaise-navy mt-1">{windowText(me?.test_window)}</div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button className="btn-secondary" onClick={() => navigator.clipboard?.writeText(a.candidate_url!)}>Copy link</button>
            <button
              className="btn-secondary"
              disabled={!me?.test_window}
              onClick={() =>
                printCandidateSheet({
                  candidateName: `${a.first_name} ${a.last_name}`.trim(),
                  testId: a.test_id,
                  url: a.candidate_url!,
                  home: a.home,
                  position: a.position,
                  windowText: windowText(me?.test_window),
                  linkText: a.expires_at ? `until ${fmtDateTime(a.expires_at)}` : `${me?.link_days ?? 14} days`,
                  timeLimitMinutes: me?.time_limit_minutes ?? 35,
                })
              }
            >
              Print sheet
            </button>
            <button className="btn-secondary" onClick={() => resendMut.mutate()} disabled={resendMut.isPending}>
              {resendMut.isPending ? "Working..." : "New link"}
            </button>
          </div>
        </div>
      )}

      {a.writing_comment && (
        <div className="card p-5">
          <div className="label">Evaluator's comment</div>
          <p className="text-sm whitespace-pre-wrap">{a.writing_comment}</p>
        </div>
      )}

      {a.integrity && (a.status === "submitted" || a.status === "evaluated" || a.integrity.started_at) && (
        <div className="card p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="label mb-0">Sitting integrity</div>
            <div className="flex gap-2 flex-wrap">
              {a.integrity.ip_changed && <Flag color="red" text="Network changed mid-test" />}
              {(a.integrity.paste_count ?? 0) > 0 && <Flag color="red" text={`Pasted text ${a.integrity.paste_count}x`} />}
              {(a.integrity.focus_loss_count ?? 0) >= 5 && <Flag color="amber" text={`Left the test ${a.integrity.focus_loss_count}x`} />}
              {a.integrity.duration_seconds != null && a.integrity.duration_seconds < 300 && (
                <Flag color="amber" text="Very fast completion" />
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-3 text-sm">
            <Field label="Consent given" value={a.integrity.consented_at ? fmtDateTime(a.integrity.consented_at) : "Not recorded"} />
            <Field
              label="Time taken"
              value={
                a.integrity.duration_seconds != null
                  ? `${Math.floor(a.integrity.duration_seconds / 60)}m ${a.integrity.duration_seconds % 60}s`
                  : "-"
              }
            />
            <Field label="Left the test window" value={a.integrity.focus_loss_count != null ? `${a.integrity.focus_loss_count}x` : "-"} />
            <Field label="Pasted into writing" value={a.integrity.paste_count != null ? `${a.integrity.paste_count}x` : "-"} />
            <Field label="Network (start)" value={a.integrity.start_ip ?? "-"} />
            <Field label="Network (submit)" value={a.integrity.submit_ip ?? "-"} />
            <Field label="Device time zone" value={a.integrity.client_tz ?? "-"} />
            <Field label="Resumed after closing" value={`${a.integrity.event_counts?.resumed ?? 0}x`} />
          </div>
          {a.integrity.user_agent && (
            <p className="text-xs text-slate-400 mt-3 truncate" title={a.integrity.user_agent}>
              Device: {a.integrity.user_agent}
            </p>
          )}
        </div>
      )}

      {(["writing", "english", "maths"] as const).map((section) =>
        grouped[section]?.length ? (
          <div key={section} className="card overflow-hidden">
            <div className="bg-liaise-navy text-white px-5 py-2.5 text-sm font-semibold capitalize">{section} answers</div>
            <div className="divide-y divide-slate-100">
              {grouped[section]
                .slice()
                .sort((x, y) => x.q_no - y.q_no)
                .map((ans) => (
                  <div key={ans.question_id} className="px-5 py-4 grid md:grid-cols-12 gap-2 text-sm">
                    <div className="md:col-span-6">
                      <span className="font-semibold text-liaise-navy mr-2">Q{ans.q_no}.</span>
                      <span className="text-slate-700">{ans.question_text}</span>
                    </div>
                    <div className="md:col-span-4">
                      <span className="label">Answer</span>
                      <span className="whitespace-pre-wrap">{prettyAnswer(ans.answer)}</span>
                    </div>
                    <div className="md:col-span-2 text-right">
                      {section === "writing" ? (
                        <WritingBadge result={a.writing_result} />
                      ) : (
                        <span
                          className={`font-bold ${
                            ans.auto_mark != null && ans.auto_mark >= ans.max_mark
                              ? "text-liaise-green"
                              : ans.auto_mark && ans.auto_mark > 0
                                ? "text-liaise-orange"
                                : "text-liaise-red"
                          }`}
                        >
                          {ans.auto_mark ?? 0} / {ans.max_mark}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
            </div>
          </div>
        ) : null,
      )}

      {reportUrl && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setReportUrl(null)}>
          <div className="bg-white rounded-xl w-full max-w-4xl h-[85vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-2 border-b border-slate-200">
              <span className="font-semibold text-liaise-navy text-sm">{a.report_file ?? `${a.test_id} report`}</span>
              <div className="flex gap-2">
                <a className="btn-secondary text-xs" href={reportUrl} download={a.report_file ?? `${a.test_id}_report.pdf`}>Download</a>
                <button className="btn-secondary text-xs" onClick={() => setReportUrl(null)}>Close</button>
              </div>
            </div>
            <iframe src={reportUrl} title="Assessment report" className="flex-1 w-full" />
          </div>
        </div>
      )}
    </div>
  );
}

function LinkQr({ url }: { url: string }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    if (ref.current) QRCode.toCanvas(ref.current, url, { width: 110, margin: 1 }, () => {});
  }, [url]);
  return <canvas ref={ref} className="rounded-lg border border-slate-200" aria-label="QR code for the candidate link" />;
}

function Flag({ color, text }: { color: "red" | "amber"; text: string }) {
  return (
    <span
      className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${
        color === "red" ? "bg-red-50 text-red-700 border-red-200" : "bg-orange-50 text-orange-700 border-orange-200"
      }`}
    >
      {text}
    </span>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="label">{label}</div>
      <div className="font-medium text-slate-800">{value}</div>
    </div>
  );
}

function ScoreRow({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="flex items-center justify-between border-b border-slate-100 last:border-0 pb-2 last:pb-0">
      <span className="text-xs uppercase tracking-wide text-slate-500">{label}</span>
      <span className={`${strong ? "text-xl" : "text-lg"} font-bold text-liaise-navy`}>{value}</span>
    </div>
  );
}
